/**
 * "이 지역에서 검색" 결과 요청(09-29).
 *
 * 예전엔 지도 버튼이 router.replace 로 페이지 전체를 서버에서 다시 그렸다 — 상품 조회
 * 말고도 허브 데이터셋·가이드·헤딩까지 다시 돌고, 경로 이름이 바뀌면(/s/seoul →
 * /s/tokyo) [region] 세그먼트가 새로 마운트돼 스켈레톤이 뜨고 지도가 다시 만들어졌다.
 * 지금은 주소만 얕게 바꾸고(url-view-sync.replaceSearchUrlShallow) 결과만
 * `/api/search/area` 에서 받는다. 이 모듈은 그 요청의 양 끝(클라이언트 조립 · 라우트
 * 검증)과 "지금 화면의 결과가 이 주소의 결과인가"를 판정하는 키를 한곳에 둔다.
 * 서버 전용 모듈을 가져오지 않는다 — 클라이언트와 라우트가 함께 쓴다.
 */
import { locales } from "@/i18n/routing";
import type { Product } from "@/types";
import type { SearchBranch } from "@/lib/api/products/branch-index";
import type { RelatedGuide } from "@/components/search/types";
import type { FallbackKind } from "@/lib/search/empty-state";

/** `/api/search/area` 응답 — 페이지가 SearchPageClient 에 넘기는 결과 props 와 같은 모양 */
export interface AreaSearchResponse {
  /** 지점은 branchIdx(아래 지점표 번호)로 온다 — 페이지 props 와 같다 */
  products: Product[];
  branchTable: SearchBranch[];
  relatedGuides: RelatedGuide[];
  fallbackProducts: Product[];
  fallbackLocationLabel: string;
  /** 폴백 출처(제목) — search-data.fallbackKind */
  fallbackKind: FallbackKind;
  regionDisplayName: string;
}

type ParamSource = URLSearchParams | { get(key: string): string | null };

/**
 * 서버 결과(상품·폴백·가이드)를 정하는 키. 이 값이 같으면 같은 결과다.
 * 뷰(zoom/clat/clng/t)·정렬·가격·날짜·허브 시트는 클라이언트에서만 쓰여 빠진다.
 * categories 도 뺀다 — 지도 영역 경로는 카테고리를 서버에서 좁히지 않고(page 주석),
 * 카테고리 칩은 얕은 replaceState 라 넣으면 칩을 누를 때마다 재조회가 나간다.
 */
const RESULT_KEYS = [
  "scope",
  "country",
  "region",
  "ne_lat",
  "ne_lng",
  "sw_lat",
  "sw_lng",
  "location_search",
  "place",
  "q",
  "search",
  "usage_method",
  "delivery_country",
] as const;

/**
 * 라우트로 보내는 키 — 결과 키 + 허브 판정용 categories. 그 밖의 키는 받지 않는다.
 * 지역 코드(`region`)는 요청에서 경로 세그먼트 이름(`region`)과 겹쳐 `admin1` 로 싣는다.
 */
const REQUEST_KEYS = [...RESULT_KEYS.filter((k) => k !== "region"), "categories"] as const;
const REGION_CODE_REQUEST_KEY = "admin1";

/** 문자열 파라미터 상한 — 검색어·카테고리 조합이 이보다 길 일은 없다. */
const MAX_PARAM_LENGTH = 200;
const SCOPES = ["city", "district", "region", "country", "nearby", "keyword", "partner"];
const BOUND_KEYS = ["ne_lat", "ne_lng", "sw_lat", "sw_lng"] as const;

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** `/{locale}/s/{region}` 의 region 세그먼트(주소 그대로, 인코딩 유지). 검색 페이지가 아니면 null. */
export function regionSegmentFromPathname(pathname: string): string | null {
  const segs = pathname.split("/").filter(Boolean);
  const i = segs.indexOf("s");
  return i >= 0 && segs[i + 1] ? segs[i + 1] : null;
}

/**
 * 결과 키. 서버(page)는 렌더한 주소로, 클라이언트는 지금 주소로 만든다 — 다르면 화면의
 * 결과가 주소보다 늦은 것이다(얕은 이동 직후, 또는 뒤로가기가 옛 렌더를 되살렸을 때).
 */
export function searchResultKey(region: string, params: ParamSource): string {
  const parts = RESULT_KEYS.map((k) => `${k}=${params.get(k) ?? ""}`);
  return `${safeDecode(region)}|${parts.join("&")}`;
}

/**
 * 범위 검색 주소인가 — 범위 4개가 있고 내 주변(NEARBY)이 아닌 것. 라우트는 이것만 받는다.
 *
 * "이 지역에서 검색"(MAP_AREA)만이 아니다: 지역·명소 알약도 얕게 바꾸는데, 명소를 고르면
 * MAP_AREA 범위가 되지만 **해제**는 지금 범위를 그대로 둔 채 place 만 뗀다(지명 검색으로
 * 들어온 district 주소면 location_search 가 없다). 범위가 있으면 서버 결과는 그 범위로
 * 정해지므로(page 의 hasBounds 분기) 라우트가 같은 함수로 그대로 낼 수 있다.
 * 내 주변은 검색바의 새 검색(router)이라 얕게 오지 않는다 — 받지 않는다.
 */
export function isBoundsSearchParams(params: ParamSource): boolean {
  const ls = params.get("location_search");
  return (ls === null || ls === "MAP_AREA") && BOUND_KEYS.every((k) => !!params.get(k));
}

/**
 * 캠핑 허브 [장비] 탭의 택배 상품 요청 표시(09-29). 이 표시가 있으면 라우트가 범위 없는
 * 주소(도시·키워드 등)도 받고 검색어 분석을 기록하지 않는다 — 사용자가 새로 검색한 것이
 * 아니라 탭을 연 것이다.
 */
export const GEAR_PURPOSE = "gear";

/** 클라이언트 → `/api/search/area` 쿼리 문자열. */
export function areaSearchRequestQuery(
  locale: string,
  region: string,
  params: ParamSource,
  options: { purpose?: typeof GEAR_PURPOSE } = {},
): string {
  const usp = new URLSearchParams();
  usp.set("locale", locale);
  usp.set("region", region);
  for (const k of REQUEST_KEYS) {
    const v = params.get(k);
    if (v) usp.set(k, v);
  }
  const regionCode = params.get("region");
  if (regionCode) usp.set(REGION_CODE_REQUEST_KEY, regionCode);
  if (options.purpose) usp.set("purpose", options.purpose);
  return usp.toString();
}

export type AreaSearchRequest =
  | {
      ok: true;
      locale: string;
      region: string;
      /** page 의 searchParams 와 같은 모양 — 허용한 키만 담긴다 */
      search: Record<string, string>;
      /** 캠핑 [장비] 탭 요청 — 범위 없는 주소 허용 · 분석 기록 안 함 */
      gear: boolean;
    }
  | { ok: false; reason: string };

/**
 * 라우트 입력 검증. 허용 키만 통과시키고, 범위는 유한한 숫자·정상 범위·ne > sw 를
 * 요구한다(값은 원문 문자열 그대로 넘겨 페이지와 같은 파싱을 타게 한다).
 */
export function parseAreaSearchRequest(params: URLSearchParams): AreaSearchRequest {
  const locale = params.get("locale") ?? "";
  if (!(locales as readonly string[]).includes(locale)) {
    return { ok: false, reason: "locale" };
  }

  const region = params.get("region") ?? "";
  if (!region || region.length > MAX_PARAM_LENGTH || /[/?#\\]/.test(region)) {
    return { ok: false, reason: "region" };
  }

  // [장비] 탭은 범위를 지운 주소(도시·키워드)에서도 부른다 — 범위가 **있으면** 같은 검증.
  const gear = params.get("purpose") === GEAR_PURPOSE;
  const hasAnyBound = BOUND_KEYS.some((k) => !!params.get(k));
  if (!gear || hasAnyBound) {
    if (!isBoundsSearchParams(params)) return { ok: false, reason: "area" };

    const [neLat, neLng, swLat, swLng] = BOUND_KEYS.map((k) => Number(params.get(k)));
    const finite = [neLat, neLng, swLat, swLng].every(Number.isFinite);
    const inRange =
      finite &&
      Math.abs(neLat) <= 90 &&
      Math.abs(swLat) <= 90 &&
      Math.abs(neLng) <= 180 &&
      Math.abs(swLng) <= 180;
    if (!inRange || neLat <= swLat || neLng <= swLng) {
      return { ok: false, reason: "bounds" };
    }
  }

  // scope 없는 주소는 페이지가 옛 스키마로 보고 308 하는 경로라 여기선 받지 않는다.
  const scope = params.get("scope");
  if (!scope || !SCOPES.includes(scope)) {
    return { ok: false, reason: "scope" };
  }

  const search: Record<string, string> = {};
  for (const k of REQUEST_KEYS) {
    const v = params.get(k);
    if (!v) continue;
    if (v.length > MAX_PARAM_LENGTH) return { ok: false, reason: k };
    search[k] = v;
  }
  const regionCode = params.get(REGION_CODE_REQUEST_KEY);
  if (regionCode) {
    if (!/^[A-Za-z]{2}-[A-Za-z0-9]{1,3}$/.test(regionCode)) {
      return { ok: false, reason: REGION_CODE_REQUEST_KEY };
    }
    search.region = regionCode;
  }
  return { ok: true, locale, region, search, gear };
}
